/* ============================================================
   La Nuit — gift-wrap.js
   <gift-wrap> custom element. Mounted inside the cart drawer.
   - Toggles a configured product line on/off (AJAX add / change qty 0).
   - Persists a free-form gift message and a "hide prices" toggle as
     cart attributes via /cart/update.js.
   - Dispatches `cart:updated` so the drawer body refreshes.
   ============================================================ */

(function () {
  if (customElements.get('gift-wrap')) return;

  function debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  }

  class GiftWrap extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.lineKey = this.dataset.lineKey || '';
      this.toggleEl = this.querySelector('[data-gift-wrap-toggle]');
      this.panel = this.querySelector('[data-gift-wrap-panel]');
      this.messageEl = this.querySelector('[data-gift-message]');
      this.hidePricesEl = this.querySelector('[data-hide-prices]');
      this.busy = false;

      this.toggleEl?.addEventListener('change', () => this.onToggle());
      this.messageEl?.addEventListener('input', debounce(() => this.saveAttrs(), 450));
      this.hidePricesEl?.addEventListener('change', () => this.saveAttrs());
    }

    async onToggle() {
      if (this.busy || !this.variantId) return;
      this.busy = true;
      this.classList.add('is-busy');
      const turnOn = !!this.toggleEl.checked;
      try {
        if (turnOn) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1, properties: { _gift_wrap: 'true' } }] })
          });
          if (this.panel) this.panel.hidden = false;
        } else {
          // Resolve a fresh line key from the live cart — handles the
          // case where the wrap was added during this same session and
          // the element's data-line-key is stale (or never set).
          let key = this.lineKey;
          if (!key) {
            try {
              const live = await window.LaNuit?.Cart.get();
              const match = (live?.items || []).find(l =>
                String(l.variant_id) === String(this.variantId) &&
                (l.properties?._gift_wrap === 'true' || l.properties?._gift_wrap === true)
              );
              if (match) key = match.key;
            } catch (_) {}
          }
          if (key) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
            if (this.panel) this.panel.hidden = true;
          }
        }
        const cart = await window.LaNuit?.Cart.get();
        if (cart) window.LaNuit?.publishCart(cart);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {
        console.error('Gift-wrap toggle failed', err);
        // Revert UI on failure
        this.toggleEl.checked = !turnOn;
      } finally {
        this.busy = false;
        this.classList.remove('is-busy');
      }
    }

    async saveAttrs() {
      const attrs = {};
      if (this.messageEl) attrs.gift_message = this.messageEl.value || '';
      if (this.hidePricesEl) attrs.hide_prices = this.hidePricesEl.checked ? 'true' : '';
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      } catch (err) {
        console.error('Gift-wrap save attrs failed', err);
      }
    }
  }

  customElements.define('gift-wrap', GiftWrap);
})();
